/* Locations, profiles, statuses */

const hasAValue = '*';
const hasNoValue = '';

function addEventScopeTo(queryString) {
  return `event:${queryString}`;
}

function addKeywordTo(queryString, keyword) {
  const includeQuotationMarks = ![hasNoValue, hasAValue].includes(queryString);
  return `${keyword}:${includeQuotationMarks ? '"' : ''}${queryString}${
    includeQuotationMarks ? '"' : ''
  }`;
}

function addLocationKeywordTo(locationString) {
  return addKeywordTo(locationString, 'location');
}

function addProfileKeywordTo(profileString) {
  return addKeywordTo(profileString, 'profile');
}

function addStatusKeywordTo(statusString) {
  return addKeywordTo(statusString, 'status');
}

function addDateKeywordTo(dateString) {
  return `date:${dateString}`;
}

function makeReductionQuery(queryString) {
  return `- ${queryString}`;
}

function makeUnionOfQueries(queryString1, queryString2) {
  return `${queryString1} + ${queryString2}`;
}

function makeIntersectionOfQueries(queryString1, queryString2) {
  return `${queryString1} ${queryString2}`;
}

function makeBracketedQuery(queryString) {
  if (queryString.length > 0) return `(${queryString})`;
  return '';
}

export function removeDeletedOptionsFromCollectionToFilterOn(
  collectionToFilterOn,
  completeCollection
) {
  const options = [];
  collectionToFilterOn.forEach((id) => {
    if (completeCollection.find((option) => option.id === id)) {
      options.push(id);
    }
  });
  return options;
}

function queryForCollection(filterCollection, completeCollection, addKeywordFn) {
  const collectionToFilterOn = removeDeletedOptionsFromCollectionToFilterOn(
    filterCollection,
    completeCollection
  );
  if (collectionToFilterOn.length === 0) return [];
  return collectionToFilterOn
    .map((itemId) => completeCollection.find((item) => item.id === itemId))
    .map((item) => {
      if (item) return addKeywordFn(item.name);
      return '';
    });
}

function queryForLocations(locationsToFilterOn, allLocations) {
  return queryForCollection(locationsToFilterOn, allLocations, (name) =>
    addLocationKeywordTo(name)
  );
}

function queryForProfiles(profilesToFilterOn, allProfiles) {
  return queryForCollection(profilesToFilterOn, allProfiles, (name) =>
    addProfileKeywordTo(name)
  );
}

function queryForStatuses(statusesToFilterOn, allStatuses) {
  return queryForCollection(statusesToFilterOn, allStatuses, (name) =>
    addStatusKeywordTo(name)
  );
}

function queryForDate(dates) {
  return dates.map((date) => addDateKeywordTo(date));
}

function unionOfQueries(queries, addScopeFn) {
  if (queries.length === 0) return '';
  return queries
    .map(addScopeFn)
    .reduce((query, subquery) => makeUnionOfQueries(query, subquery));
}

function intersectionOfQueries(...args) {
  if (args.length === 1) return args[0];
  return args.reduce(
    (query, subquery) => makeIntersectionOfQueries(query, makeBracketedQuery(subquery)),
    ''
  );
}

export function buildQuery(scope, keywordToOptionsMapping) {
  let addScopeFn = (query) => query;
  if (scope === 'event') addScopeFn = addEventScopeTo;

  const queryPerKeyword = Object.keys(keywordToOptionsMapping)
    // eslint-disable-next-line no-unused-vars
    .sort((keyword1, keyword2) => {
      if (keyword1 === 'date') return -1;
      return 0;
    })
    .map((keyword) => {
      const options = keywordToOptionsMapping[keyword];
      switch (keyword) {
        case 'date':
          return queryForDate(options.filterOn);
        case 'location':
          return queryForLocations(options.filterOn, options.all);
        case 'profile':
          return queryForProfiles(options.filterOn, options.all);
        case 'status':
          return queryForStatuses(options.filterOn, options.all);
        default:
          return [];
      }
    })
    .map((queriesPerKeyword) => unionOfQueries(queriesPerKeyword, addScopeFn));
  return intersectionOfQueries(...queryPerKeyword).trim();
}
